/**
 * EL MOTOR DEL CUADERNO (CQ2): de las filas de Pest al `CuadernoData` que
 * estampan el PDF de la Junta y su Excel (CQ3), con lo que falta y de dónde sale
 * cada valor.
 *
 * PURO Y SIN RED: recibe `EntradaCuaderno` (ya leída de Supabase por quien lo
 * llame, CQ4) y devuelve:
 *   - `data`: el cuaderno. Lo que no se sabe va VACÍO, nunca inventado; lo que
 *     no pasa un validador (NIF con el control mal, ROPO basura, nº de registro
 *     de otro producto) tampoco se imprime;
 *   - `origenes`: de dónde sale cada valor (dato | calculo | defecto | usuario),
 *     para la hoja de revisión que el titular mira antes de firmar (D23);
 *   - `huecos` y `preguntas`: qué falta y qué preguntar, agrupado como el guion
 *     del PLAN §3;
 *   - `cobertura`: cuántas filas del 3.1 salen completas (la medida del plan).
 *
 * D14: el cuaderno es de UN titular y de un año natural (o una campaña). Las
 * fincas de otro titular se quedan fuera (`fincasDelTitular`).
 */
import type { AplicadorEntrada, EntradaCuaderno, EquipoEntrada, ParteEntrada } from "./entrada";
import { parsePerLocation, type DatosFinca } from "./datosLegales";
import { SIN_DATOS, type Hueco, type Origen, type Pregunta, type Seccion } from "./modeloOficial";
import { planDeParcelas, recintosConfirmados, repartoEntreRecintos, type ParcelaPest } from "./parcelOrder";
import { claveNombre, construirFilas31, edicionDe, filaCompleta, type MetaFila31, type Tratamiento31 } from "./rows31";
import { romaExenta, siguientesPreguntas } from "./huecos";
import { checkNif, checkRoma, checkRopo, isValidCodigoPostal } from "./validators";
import { especieOficial } from "./cultivos";
import type { CuadernoDataInput } from "./types";

export interface ResultadoCuaderno {
  data: CuadernoDataInput;
  origenes: Record<string, Origen>;
  huecos: Hueco[];
  preguntas: Pregunta[];
  /** Filas y partes del 3.1 con TODO lo que pide el modelo. */
  cobertura: { filas: number; filasCompletas: number; partes: number; partesCompletas: number };
  /** Meta de cada fila del 3.1 (mismo índice que `data["3.1"]`): líneas, partes, huecos, avisos del nº. */
  meta31: MetaFila31[];
  periodo: { desde: string; hasta: string; etiqueta: string };
  /** Líneas que no entran y por qué. */
  fuera: { linea: string; motivo: "excluida" | "sin_nave" }[];
  /** Las que el borrador quería quitar y no salieron. Desde CU9 (28-09-2026) siempre vacía: sale cualquiera. */
  noSePuedenQuitar: string[];
}

const limpio = (v: string | null | undefined) => {
  const s = String(v ?? "").trim();
  return s || null;
};

const DEFECTO_RIEGO = "LOC" as const;
const DEFECTO_PROTECCION = "INV" as const;

export function construirCuaderno(e: EntradaCuaderno): ResultadoCuaderno {
  const origenes: Record<string, Origen> = {};
  const huecos: Hueco[] = [];
  const hueco = (h: Hueco) => huecos.push(h);

  // ── Periodo y alcance del titular (D14) ────────────────────────────────────
  const periodo = e.campana
    ? { desde: e.campana.desde, hasta: e.campana.hasta, etiqueta: e.campana.etiqueta }
    : { desde: `${e.anio}-01-01`, hasta: `${e.anio}-12-31`, etiqueta: String(e.anio) };
  const fincasIds = new Set(e.fincasDelTitular ?? e.fincas.map((f) => f.id));
  const fincas = e.fincas.filter((f) => fincasIds.has(f.id));
  const naves = e.naves.filter((n) => (e.fincasDelTitular ? n.locationId != null && fincasIds.has(n.locationId) : true));
  const navesIds = new Set(naves.map((n) => n.id));
  const sectores = e.sectores.filter((s) => navesIds.has(s.naveId));
  const naveDeSector = new Map(sectores.map((s) => [s.id, s.naveId]));
  const partes: ParteEntrada[] = e.partes.filter((p) => {
    if (p.fecha < periodo.desde || p.fecha > periodo.hasta) return false;
    const naveId = p.naveId ?? (p.blockId ? naveDeSector.get(p.blockId) : null);
    return !!naveId && navesIds.has(naveId);
  });

  const plan = planDeParcelas({ periodo, fincas, naves, sectores, ciclos: e.ciclos, partes });
  const L = e.legal ?? null;

  // ── 1.1 Datos generales ────────────────────────────────────────────────────
  const nif = checkNif(L?.nif);
  const titularNif = checkNif(L?.titular_nif);
  const cp = isValidCodigoPostal(L?.codigo_postal) ? limpio(L?.codigo_postal) : null;
  const cpTitular = isValidCodigoPostal(L?.titular_codigo_postal) ? limpio(L?.titular_codigo_postal) : null;
  const d11 = {
    fechaApertura: periodo.desde,
    razonSocial: limpio(L?.razon_social),
    nif: nif.ok ? nif.value : null,
    reaNacional: limpio(L?.rea_nacional),
    reaAutonomico: limpio(L?.reafa),
    direccion: limpio(L?.direccion),
    localidad: limpio(L?.localidad),
    codigoPostal: cp,
    provincia: limpio(L?.provincia),
    telefonoFijo: limpio(L?.telefono_fijo),
    telefonoMovil: limpio(L?.telefono_movil),
    email: limpio(L?.email),
    titular: {
      nombre: limpio(L?.titular_nombre),
      nif: titularNif.ok ? titularNif.value : null,
      direccion: limpio(L?.titular_direccion),
      localidad: limpio(L?.titular_localidad),
      codigoPostal: cpTitular,
      provincia: limpio(L?.titular_provincia),
      tipoRepresentacion: limpio(L?.representacion_tipo),
      telefono: limpio(L?.titular_telefono),
      email: limpio(L?.titular_email),
    },
  };
  origenes["1.1.fechaApertura"] = "calculo";
  for (const [campo, valor] of Object.entries(d11)) {
    if (campo === "titular" || campo === "fechaApertura") continue;
    if (valor) origenes[`1.1.${campo}`] = "usuario";
  }
  for (const [campo, valor] of Object.entries(d11.titular)) if (valor) origenes[`1.1.titular.${campo}`] = "usuario";
  const obligatorio11: [keyof typeof d11, string | null | undefined][] = [
    ["razonSocial", L?.razon_social], ["nif", L?.nif], ["direccion", L?.direccion], ["localidad", L?.localidad],
    ["codigoPostal", L?.codigo_postal], ["provincia", L?.provincia], ["reaNacional", L?.rea_nacional],
  ];
  for (const [campo, crudo] of obligatorio11) {
    if (!d11[campo]) hueco({ seccion: "1.1", campo, motivo: limpio(crudo) ? "invalido" : "falta" });
  }
  if (!d11.titular.nombre) hueco({ seccion: "1.1", campo: "titular.nombre", motivo: "falta" });
  if (!d11.titular.nif) hueco({ seccion: "1.1", campo: "titular.nif", motivo: limpio(L?.titular_nif) ? "invalido" : "falta" });

  // ── 1.2 Personas y 1.3 Equipos: los que aparecen en los partes ─────────────
  // CU2: también los DEDUCIDOS (el parte no los guardó): quien solo sale deducido
  // lleva origen «deducido» en su nombre o descripción (confírmalo).
  const aplicadores = distintos(partes.map((p) => p.aplicador).filter(Boolean) as AplicadorEntrada[]);
  const equipos = distintos(partes.map((p) => p.equipo).filter(Boolean) as EquipoEntrada[]);

  /* CU9: el aplicador o el equipo que la PERSONA pone en una línea del documento, por
     su nombre. Si ya está en los partes, es ese; si está dado de alta en la empresa,
     entra con su ficha (NIF, ROPO, ROMA); si no, entra con el nombre tal cual y lo
     demás como hueco. */
  const aplicadorPorNombre = new Map<string, string>();
  const equipoPorNombre = new Map<string, string>();
  const partesIds = new Set(partes.map((p) => p.id));
  const excluidasIds = e.decisiones?.excluidas ?? {};
  for (const l of e.lineas) {
    if (!partesIds.has(l.parteId) || Object.prototype.hasOwnProperty.call(excluidasIds, l.id)) continue;
    const ed = edicionDe(l, e.decisiones?.ediciones);
    if (ed?.aplicador) {
      const k = claveNombre(ed.aplicador);
      if (!aplicadorPorNombre.has(k)) {
        const hit = aplicadores.find((a) => claveNombre(a.nombre) === k) ?? (e.aplicadoresEmpresa ?? []).find((a) => claveNombre(a.nombre) === k);
        const a = hit ?? { id: `usuario:${k}`, nombre: ed.aplicador };
        if (!aplicadores.some((x) => x.id === a.id)) aplicadores.push(a);
        aplicadorPorNombre.set(k, a.id);
      }
    }
    if (ed?.equipo) {
      const k = claveNombre(ed.equipo);
      if (!equipoPorNombre.has(k)) {
        const hit = equipos.find((q) => claveNombre(q.descripcion) === k) ?? (e.equiposEmpresa ?? []).find((q) => claveNombre(q.descripcion) === k);
        const q = hit ?? { id: `usuario:${k}`, descripcion: ed.equipo };
        if (!equipos.some((x) => x.id === q.id)) equipos.push(q);
        equipoPorNombre.set(k, q.id);
      }
    }
  }
  aplicadores.sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  equipos.sort((a, b) => a.descripcion.localeCompare(b.descripcion, "es"));
  const guardados = {
    aplicadores: new Set(partes.filter((p) => p.aplicador && !p.deducido?.aplicador).map((p) => p.aplicador!.id)),
    equipos: new Set(partes.filter((p) => p.equipo && !p.deducido?.equipo).map((p) => p.equipo!.id)),
  };
  const deLaPersona = { aplicadores: new Set(aplicadorPorNombre.values()), equipos: new Set(equipoPorNombre.values()) };
  const ordenAplicador = new Map(aplicadores.map((a, i) => [a.id, i + 1]));
  const ordenEquipo = new Map(equipos.map((q, i) => [q.id, i + 1]));

  const d12 = aplicadores.map((a, i) => {
    const n = checkNif(a.nif);
    const r = checkRopo(a.ropo);
    if (!n.ok) hueco({ seccion: "1.2", campo: "nif", fila: i, motivo: limpio(a.nif) ? "invalido" : "falta" });
    if (!r.ok) hueco({ seccion: "1.2", campo: "ropo", fila: i, motivo: limpio(a.ropo) ? "invalido" : "falta" });
    if (!a.carne) hueco({ seccion: "1.2", campo: "carne", fila: i, motivo: "falta" });
    if (!guardados.aplicadores.has(a.id)) origenes[`1.2.${i}.nombre`] = deLaPersona.aplicadores.has(a.id) ? "usuario" : "deducido";
    return { orden: i + 1, nombre: a.nombre.trim() || "—", nif: n.ok ? n.value : null, ropo: r.ok ? r.value : null, carne: a.carne ?? null, asesor: false };
  });
  const d13 = equipos.map((q, i) => {
    const exenta = romaExenta(q.roma);
    const r = checkRoma(q.roma);
    if (!exenta && !r.ok) hueco({ seccion: "1.3", campo: "roma", fila: i, motivo: limpio(q.roma) ? "invalido" : "falta" });
    if (!q.fechaAdquisicion) hueco({ seccion: "1.3", campo: "fechaAdquisicion", fila: i, motivo: "falta" });
    if (!guardados.equipos.has(q.id)) origenes[`1.3.${i}.descripcion`] = deLaPersona.equipos.has(q.id) ? "usuario" : "deducido";
    return {
      orden: i + 1,
      descripcion: q.descripcion.trim() || "—",
      roma: exenta ? "Exenta" : r.ok ? r.value : null,
      fechaAdquisicion: q.fechaAdquisicion ?? null,
      fechaUltimaInspeccion: q.fechaUltimaInspeccion ?? null,
    };
  });

  // ── 1.4 Asesor: solo con nº ROPO de asesor (D18) ───────────────────────────
  const ropoAsesor = checkRopo(L?.asesor_ropo);
  const asesor = limpio(L?.asesor_nombre) && ropoAsesor.ok ? { nombre: limpio(L?.asesor_nombre) as string, ropo: ropoAsesor.value } : null;
  const gip = (["AE", "PI", "CP", "Atrias", "AS", "NO"] as const).find((g) => g === L?.gip_tipo) ?? null;
  const nifAsesor = checkNif(L?.asesor_nif);
  const d14 = asesor ? [{ nombre: asesor.nombre, nif: nifAsesor.ok ? nifAsesor.value : null, identificacion: asesor.ropo, tipoExplotacion: gip }] : [];
  if (limpio(L?.asesor_nombre) && !ropoAsesor.ok) hueco({ seccion: "1.4", campo: "identificacion", motivo: limpio(L?.asesor_ropo) ? "invalido" : "falta" });
  if (!gip) hueco({ seccion: "2.1", campo: "sistemaGip", motivo: "falta" });

  // ── 2.1 Parcelas: una fila por recinto confirmado, repitiendo el nº ─────────
  const confirmados = recintosConfirmados(e.recintos);
  const hayDerivados = new Set((e.recintos ?? []).filter((r) => !r.confirmadoEn).map((r) => r.greenhouseId));
  const riego = (["SEC", "ASP", "LOC", "GRA"] as const).find((r) => r === L?.riego_defecto) ?? null;
  const proteccion = (["AL", "M", "BP", "INV"] as const).find((r) => r === L?.proteccion_defecto) ?? null;
  const d21: Record<string, unknown>[] = [];
  const navesSinSigpac = new Set<string>();
  for (const p of plan.parcelas) {
    const recs = confirmados.get(p.naveId) ?? [];
    // CU2: la especie en castellano oficial («Pomodoro» → Tomate, «Brocoli» → Brócoli).
    // F4 cultivos SIEX: con código, manda el código (166 → «Acelga» aunque el texto diga otra cosa).
    const especie = especieOficial(p.especie, p.especieCodigo ?? null);
    const comun = {
      orden: p.orden,
      superficieCultivadaHa: p.areaM2 != null ? p.areaM2 / 10000 : null,
      especie: especie.nombre,
      variedad: p.variedad,
      secanoRegadio: riego ?? DEFECTO_RIEGO,
      aireLibreProtegido: proteccion ?? DEFECTO_PROTECCION,
      sistemaGip: gip,
    };
    /* Varios recintos: cada uno con LO SUYO de la superficie cultivada, no la nave
       entera repetida (25-10-2026). Sin saber el reparto, vacía y se pide. */
    const reparto = repartoEntreRecintos(recs);
    const filas = recs.length
      ? recs.map((r, ri) => ({
          ...comun,
          superficieCultivadaHa:
            comun.superficieCultivadaHa == null ? null : reparto ? comun.superficieCultivadaHa * reparto[ri] : recs.length === 1 ? comun.superficieCultivadaHa : null,
          sigpac: {
            provincia: r.provincia.padStart(2, "0"),
            terminoMunicipal: [r.municipioCodigo, limpio(r.municipioNombre)].filter(Boolean).join(" "),
            agregado: r.agregado,
            zona: r.zona,
            poligono: r.poligono,
            parcela: r.parcela,
            recinto: r.recinto,
          },
          usoSigpac: limpio(r.usoSigpac),
          superficieSigpacHa: r.superficieHa ?? null,
        }))
      : [{ ...comun, sigpac: {} }];
    for (const f of filas) {
      const i = d21.length;
      d21.push(f);
      origenes[`2.1.${i}.orden`] = "calculo";
      origenes[`2.1.${i}.superficieCultivadaHa`] = "calculo";
      if (especie.nombre) origenes[`2.1.${i}.especie`] = "dato";
      origenes[`2.1.${i}.secanoRegadio`] = riego ? "usuario" : "defecto";
      origenes[`2.1.${i}.aireLibreProtegido`] = proteccion ? "usuario" : "defecto";
      if (gip) origenes[`2.1.${i}.sistemaGip`] = "usuario";
      if (recs.length) origenes[`2.1.${i}.sigpac`] = "usuario";
    }
    if (recs.length > 1 && !reparto && comun.superficieCultivadaHa != null) {
      for (let i = d21.length - filas.length; i < d21.length; i++) {
        hueco({
          seccion: "2.1",
          campo: "superficieCultivadaHa",
          fila: i,
          motivo: "falta",
          detalle: "La nave está en varios recintos: confírmalos desde «Datos» de la nave para repartir su superficie entre ellos",
        });
      }
    }
    if (!recs.length) {
      navesSinSigpac.add(p.naveId);
      hueco({ seccion: "2.1", campo: "sigpac", fila: d21.length - 1, motivo: hayDerivados.has(p.naveId) ? "sin_confirmar" : "falta" });
    }
    if (!especie.nombre) hueco({ seccion: "2.1", campo: "especie", fila: d21.length - 1, motivo: "falta" });
    /* Hueco SUAVE: un cultivo que no está en la tabla oficial se imprime tal cual (con
       mayúscula) y la hoja de revisión lo enseña «sin confirmar»; no para nada. */
    else if (!especie.reconocida) {
      const primera = d21.length - filas.length;
      for (let i = primera; i < d21.length; i++) {
        hueco({ seccion: "2.1", campo: "especie", fila: i, motivo: "sin_confirmar", detalle: "Cultivo que no está en la lista oficial: se imprime tal cual" });
      }
    }
  }

  // ── 2.2 Captaciones y zonas, por finca (por defecto NO) ────────────────────
  const perLocation = parsePerLocation(L?.per_location);
  const fincasSin22: string[] = [];
  const grupos22 = new Map<string, { datos: DatosFinca; parcelas: number[]; porDefecto: boolean }>();
  for (const f of fincas) {
    const suyas = plan.parcelas.filter((p) => p.fincaId === f.id).map((p) => p.orden);
    if (!suyas.length) continue;
    const guardado = perLocation[f.id];
    if (!guardado) fincasSin22.push(f.id);
    const datos: DatosFinca = guardado ?? { captacion: false, zona: "no" };
    const k = JSON.stringify([datos, !guardado]);
    const g = grupos22.get(k) ?? { datos, parcelas: [], porDefecto: !guardado };
    g.parcelas.push(...suyas);
    grupos22.set(k, g);
  }
  const todasLasParcelas = plan.parcelas.map((p) => p.orden);
  const d22 = [...grupos22.values()].map((g, i) => {
    origenes[`2.2.${i}`] = g.porDefecto ? "defecto" : "usuario";
    const cubreTodas = todasLasParcelas.length > 1 && todasLasParcelas.every((o) => g.parcelas.includes(o));
    return {
      parcelas: cubreTodas ? ("TODAS" as const) : [...new Set(g.parcelas)].sort((a, b) => a - b),
      captacionEnParcela: g.datos.captacion,
      distanciaM: g.datos.captacion_distancia_m ?? null,
      coordenadasUtm: g.datos.captacion_utm ?? null,
      denominacion: g.datos.captacion_denominacion ?? null,
      zonaTotalmente: g.datos.zona === "total",
      zonaParcialmente: g.datos.zona === "parcial",
      zonaParcialmenteHa: g.datos.zona === "parcial" ? g.datos.zona_ha ?? null : null,
    };
  });

  // ── 3.1, 3.1 bis y 7 ───────────────────────────────────────────────────────
  const f31 = construirFilas31({ entrada: e, partes, plan, ordenAplicador, ordenEquipo, asesor, aplicadorPorNombre, equipoPorNombre });
  f31.filas.forEach((_, i) => {
    const m = f31.meta[i];
    for (const [campo, o] of Object.entries(m.origenes)) if (o) origenes[`3.1.${i}.${campo}`] = o;
    for (const h of m.huecos) hueco({ seccion: "3.1", campo: h.campo, fila: i, motivo: h.motivo, partes: m.partes, lineas: m.lineas });
  });

  // ── 3.2 a 3.5: por defecto NO, confirmado una vez (D15) ────────────────────
  const aplica = (k: "3.2" | "3.3" | "3.4" | "3.5") => {
    const v = e.decisiones?.aplica?.[k];
    origenes[`${k}.aplica`] = v == null ? "defecto" : "usuario";
    return { aplica: v ?? false, filas: [] };
  };

  // ── Notas al pie: D19 y las sueltas sin asesor (D20) ───────────────────────
  const notas: Partial<Record<Seccion | "doc", string>> = { "5": SIN_DATOS, "8": SIN_DATOS };
  if (!f31.fertilizacion.length) notas["7"] = SIN_DATOS;
  if (f31.sueltasSueltas.length) notas["3.1"] = `Sueltas de auxiliares (alternativa no química): ${f31.sueltasSueltas.join("; ")}`;

  const explotacion = d11.razonSocial ?? d11.titular.nombre;
  const data: CuadernoDataInput = {
    cabecera: { explotacion, nif: d11.nif ?? d11.titular.nif, anio: periodo.etiqueta },
    "1.1": d11,
    "1.2": d12,
    "1.3": d13,
    "1.4": d14,
    "2.1": d21 as CuadernoDataInput["2.1"],
    "2.2": d22,
    "3.1": f31.filas,
    "3.1bis": {
      filas: f31.bis as NonNullable<CuadernoDataInput["3.1bis"]>["filas"],
      ...(asesor && f31.bis.length ? { validacionIntermedia: { asesor: asesor.nombre, ropo: asesor.ropo }, validacionFinal: { asesor: asesor.nombre, ropo: asesor.ropo } } : {}),
    },
    "3.2": aplica("3.2"),
    "3.3": aplica("3.3"),
    "3.4": aplica("3.4"),
    "3.5": aplica("3.5"),
    "7": { aplicaciones: f31.fertilizacion as NonNullable<CuadernoDataInput["7"]>["aplicaciones"] },
    notas: notas as CuadernoDataInput["notas"],
  };

  const preguntas = siguientesPreguntas({
    legal: L,
    aplicadores,
    equipos,
    aplicadoresEmpresa: e.aplicadoresEmpresa ?? [],
    equiposEmpresa: e.equiposEmpresa ?? [],
    navesSinSigpac: [...navesSinSigpac],
    fincasSin22,
    filas31: f31.filas as Tratamiento31[],
    meta31: f31.meta,
    decisiones: e.decisiones,
    periodo,
  });

  const completas = f31.meta.filter(filaCompleta);
  const partesConFila = new Set(f31.meta.flatMap((m) => m.partes));
  const partesIncompletas = new Set(f31.meta.filter((m) => !filaCompleta(m)).flatMap((m) => m.partes));

  return {
    data,
    origenes,
    huecos,
    preguntas,
    cobertura: {
      filas: f31.filas.length,
      filasCompletas: completas.length,
      partes: partesConFila.size,
      partesCompletas: [...partesConFila].filter((p) => !partesIncompletas.has(p)).length,
    },
    meta31: f31.meta,
    periodo,
    fuera: f31.fuera,
    noSePuedenQuitar: f31.noSePuedenQuitar,
  };
}

/** Sin repetir por id, conservando el primero. */
function distintos<T extends { id: string }>(xs: T[]): T[] {
  const m = new Map<string, T>();
  for (const x of xs) if (!m.has(x.id)) m.set(x.id, x);
  return [...m.values()];
}

export type { ParcelaPest };
